import React, { useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import DetailCostFormCst from "../DetailCostFormCst";
import {
  useFetchDataDetail,
  useFetchDetail,
  usePostDetail,
} from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAddEditDetailCost } from "../hooks/useAddEditDetailCost";

const AddDetailCost: React.FC = () => {
  const { t } = useTranslation(["projects-cost-detail"]);
  const { id, idProject, idCostModel } = useParams();
  const navigate = useNavigate();
  const { columns, actionsForForms } = useFetchDetail(
    ServicesURL.getCostModelDetail,
    undefined,
    PagesID["projects.cost-detail"],
  );

  /*Get the project information*/
  const { dataDetail: dataProject } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  /*Get the cost model information*/
  const { dataDetail: dataCostModel } = useFetchDataDetail(
    `${ServicesURL.postCostModel.replace(":budgetProjectId", String(idProject))}/${idCostModel}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetContractAssociations,
    setSupplierAssociation,
    setContractAssociation,
    contractAssociation,
    supplierAssociation,
    openAssociations,
  } = useAddEditDetailCost([], dataCostModel);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { trigger: triggerCostDetail } = usePostDetail(
    ServicesURL.postCostModelDetail,
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToSend: any = {
      title: inputForm.title || null,
      cost_model_item_id: idCostModel ? parseInt(idCostModel, 10) : null,
      status: inputForm.status || null,
      management_type: inputForm.management_type || null,
      qt: parseInt(inputForm.qt, 10) || 0,
      unit_cost: parseFloat(inputForm.unit_cost) || 0,
      supplier_id:
        supplierAssociation && supplierAssociation.length > 0
          ? parseInt(supplierAssociation[0].id)
          : null,
      contract_id:
        contractAssociation && contractAssociation.length > 0
          ? parseInt(contractAssociation[0].id)
          : null,
      notes: inputForm.notes || null,
    };

    try {
      await triggerCostDetail(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  /*Building the data to send at the form with project and costmodel info*/
  const formField = useMemo(() => {
    if (dataCostModel.dto != null && dataProject.dto != null) {
      return {
        info_project: dataProject?.dto || null,
        info_cost_model: dataCostModel?.dto || null,
      };
    } else {
      return null;
    }
  }, [dataCostModel, dataProject, dataProject]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-contracts.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("add.hero-title")}
          description={t("add.hero-description")}
        />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <DetailCostFormCst
              row={formField}
              columns={columns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(-1);
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              resetContractAssociations={resetContractAssociations}
              association={openAssociations}
              fullWidth={true}
              supplierAssociation={supplierAssociation}
              setContractAssociation={setContractAssociation}
              isNew={true}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddDetailCost;
